import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
  styled,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";

import RequestCard from "../../Cards/RequestCard";
import Panel, { PanelProps } from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import StartIcon from "@mui/icons-material/Start";
import moment from "moment";
import { FC, MouseEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { throttle } from "throttle-debounce";
import { cacheService, ingestService, searchService, thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { DbIngestConfigPackInfos } from "../../../Core/Api/Types/db-ingest-config-pack-infos";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ComputeValueBaseTypeFromMetaBaseType, GetValueBaseTypeFromMetaBaseType } from "../../../Core/Utils/functions";
import {
  FetchLightPackPlaceholders,
  FetchNonOptionalPlaceholder,
  FetchSelectedTitles,
  GetFetchedPacks,
  GetSelectedTitles,
  RemoveTitleByName,
  SelectPackById,
  SetFetchedPacks,
} from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { routePaths } from "../../../Router/router";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import DrawerWrapper from "../../Drawers/Drawer/drawer-wrapper";
import { useTranslation } from "react-i18next";

export interface NewRequestPanelProps extends PanelProps {
  type: "create" | "update";
  onClose?: MouseEventHandler;
}

const NewRequestPanel: FC<NewRequestPanelProps> = (props) => {
  const { t } = useTranslation();
  const { type, onClose } = props;

  // steps
  const [activeStep, setActiveStep] = useState(0);
  // #region States - Step1
  // search
  const titleTypeMeta = useMemo(() => cacheService.GetMetadata().find((m) => m.tagName === "titleType"), []);
  const [titleType, setTitleType] = useState("Movie");
  const [searchInput, setSearchInput] = useState("");
  // selected title
  const [inputLoading, setInputLoading] = useState(false);
  const [fetchedTitles, setFetchedTitles] = useState<DbAssetObjectInfos[]>([]);
  const selectedTitles = useAppSelector(GetSelectedTitles);
  // #endregion
  // #region States - Step2
  // selectedPacks
  const [searchPackValue, setSearchPackValue] = useState("");
  const fetchedPacks = useAppSelector(GetFetchedPacks);
  const [selectedPackId, setSelectedPackId] = useState(0);
  // #endregion

  // dispatch
  const dispatch = useAppDispatch();
  // navigate
  const navigate = useNavigate();
  // snackbar
  const enqueueSnackbar = useCustomSnackbar();
  // memo
  const selectedTitlesIds = useMemo(() => selectedTitles.flatMap((x) => x.id), [selectedTitles]);
  const neededMetas = useMemo(
    () =>
      cacheService
        .GetMetadata()
        .filter(
          (x) => x.tagName && ["productionDate", "titleType", "serieSeasonEpisodeTotalNumber"].includes(x.tagName)
        ) as DbMetadataFieldInfos[],
    []
  );

  // #region Functions - Step1
  // handle type change
  const handleSearchTypeChange = useCallback(
    (titleType: string) => {
      setTitleType(titleType);
      searchTitles(searchInput);
      if (fetchedTitles) setFetchedTitles([]);
    },
    [fetchedTitles, searchInput]
  );

  // search titles
  const searchTitles = useCallback(
    (searchValue: string) => {
      if (!searchValue || searchValue.isEmpty() /*|| searchValue.length <= 1*/) return;

      setInputLoading(true);
      searchService
        .SearchBasic(
          new SearchRequestNative({
            pattern: searchValue,
            outputLayer: ObjectLayer.Title,
            hideTitleWithoutAsset: false,
            metadata: [
              {
                $type: ComputeValueBaseTypeFromMetaBaseType(BaseMetadataType.DbStringMetadataValueInfos),
                id: titleTypeMeta?.id || 0,
                fieldType: GetValueBaseTypeFromMetaBaseType(BaseMetadataType.DbStringMetadataValueInfos),
                value: titleType,
                creationTime: new Date().toISOString(),
                updateTime: null,
                fieldName: null,
                fieldLabel: null,
                targetLayer: ObjectLayer.Title,
                valueLabel: null,
              },
            ],
          })
        )
        .then((response) => {
          setInputLoading(false);
          if (!response.success) {
            enqueueSnackbar(response.message || t("alert:error.somethingWentWrong"), "error");
          } else {
            setFetchedTitles(response.results !== null ? response.results : []);
            console.debug("Search found " + response.results?.length + " titles");
          }
        });
    },
    [titleType, titleTypeMeta]
  );

  // throttle
  const throttleSearchTitles = useMemo(
    () =>
      throttle(GlobalConst.SEARCH_THROTTLE_DELAY_MS, searchTitles, {
        noLeading: true,
      }),
    [searchTitles]
  );

  const handleSearchInputChange = (searchValue: string) => {
    setSearchInput(searchValue);
    throttleSearchTitles(searchValue);
  };

  const handleSearchValueChange = (selectedObjects: DbAssetObjectInfos[] | null) => {
    if (
      selectedObjects &&
      selectedObjects.filter((x) => x.confidentialityInfos !== null).length > 0 &&
      !UserHasAcl(AclTypes.aclTitleConfidentialityIngestRequestCreation)
    ) {
      enqueueSnackbar(t("alert:warning.notAllowedToCreateRequests"), "warning");
      return;
    }

    dispatch(FetchSelectedTitles(selectedObjects !== null ? selectedObjects : []));
  };
  // #endregion

  // #region Functions - Step2
  const handlePackOnClick = (pack: DbIngestConfigPackInfos) => {
    if (selectedPackId == pack.id) setSelectedPackId(0);
    else setSelectedPackId(pack.id);
  };

  useEffect(() => {
    ingestService.GetConfigPackList().then((response) => dispatch(SetFetchedPacks(response.packs || [])));
  }, []);
  // #endregion

  const handleNextStep = () => {
    if (activeStep === 0) setActiveStep(1);
    else {
      if (selectedPackId > 0) {
        dispatch(SelectPackById(selectedPackId));
        dispatch(FetchNonOptionalPlaceholder(selectedPackId));
        dispatch(FetchLightPackPlaceholders(selectedPackId));
      }
      navigate(routePaths.ingestNewRequest.path);
    }
  };

  return (
    <DrawerWrapper
      anchor="right"
      header={
        <PanelHeader
          title={t("panel:title.createRequest")}
          onClose={onClose as PanelHeaderProps["onClose"]}
          rightContent={
            type === "create" && (
              <Button
                variant="outlined"
                endIcon={<StartIcon />}
                onClick={(_) => navigate(routePaths.ingestNewRequest.path)}
              >
                {t("common:skip")}
              </Button>
            )
          }
        />
      }
      footerContent={
        <>
          {activeStep >= 1 && (
            <Button variant="icon" onClick={() => setActiveStep(0)}>
              <ArrowBackIcon />
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            endIcon={<ArrowForwardIcon />}
            onClick={type === "create" ? handleNextStep : onClose}
            disabled={(activeStep === 0 && selectedTitles.length === 0) || (activeStep === 1 && selectedPackId === 0)}
          >
            {type === "create" ? t("button:nextStep") : t("button:confirmChanges")}
          </Button>
        </>
      }
      open={props.open}
      onClose={onClose}
    >
      <Box flex={1}>
        {activeStep === 0 && (
          <>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
              {t("field:label.selectTitles")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <FormLabel id="movies-seasons-radios-label_">{t("field:label.titleTypeLowerCase")}:</FormLabel>
              <RadioGroup
                row
                aria-labelledby="movies-seasons-radios-label"
                //defaultValue="Movie"
                name="radio-buttons-group"
                value={titleType}
                onChange={(event, value) => handleSearchTypeChange(value)}
              >
                <FormControlLabel
                  sx={{ color: "text.tier" }}
                  value="Movie"
                  control={<Radio disabled={selectedTitles.length > 0} />}
                  label={t("common:movie")}
                />
                <FormControlLabel
                  sx={{ color: "text.tier" }}
                  value="Season"
                  control={<Radio disabled={selectedTitles.length > 0} />}
                  label={t("common:season")}
                />
              </RadioGroup>
            </Stack>
            <StyledAutocomplete
              // options
              multiple
              options={fetchedTitles}
              noOptionsText={
                searchInput.length > 0 ? t("field:helper.noTitlesFound") : t("field:helper.startTypingToSearchTitle")
              }
              loading={inputLoading}
              loadingText={t("field:helper.searchingForTitles")}
              getOptionLabel={(option) =>
                `${option.titleCustomerId ? `#${option.titleCustomerId} - ` : ""} ${option.name} (${moment(
                  GetMetadataByTagName(option, "productionDate", neededMetas)?.value
                ).year()})${
                  GetMetadataByTagName(option, "titleType", neededMetas)?.value === "Season"
                    ? ` - ${GetMetadataByTagName(option, "serieSeasonEpisodeTotalNumber", neededMetas)?.value || 0} ep`
                    : ""
                }`
              } // only display title Name
              getOptionDisabled={(option) => selectedTitlesIds.includes(option.id)} // disable options that are already selected (useful in the case we changed search list)
              isOptionEqualToValue={(option) => selectedTitlesIds.includes(option.id)} // used to hilight option when new search retrieve same results
              // input - Text entered by user
              inputValue={searchInput}
              onInputChange={(event, value) => handleSearchInputChange(value)}
              filterOptions={(options, object) => options}
              // value - Titles selectioned by user
              value={selectedTitles}
              onChange={(event, value) => handleSearchValueChange(value)}
              renderTags={() => <></>} // use to not display selected values
              // ui props
              disablePortal
              disableClearable // we don't want the user to be able to clear selection
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  endAdornment={
                    <>
                      {inputLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  }
                  inputProps={params.inputProps}
                  placeholder={t("field:placeholder.searchTitles")}
                  sx={{ width: "100%" }}
                />
              )}
              popupIcon={
                <>
                  <Divider orientation="vertical" sx={{ height: 20, mr: 1 }} />
                  <SearchIcon />
                </>
              }
              sx={{ flex: 1, mb: 1 }}
            />

            {selectedTitles.length > 0 && (
              <Box bgcolor="backgroundAlpha.main" borderRadius="6px" p={2}>
                {selectedTitles.map((title, index) => (
                  <RequestCard
                    key={index}
                    id={title.name}
                    sx={{ mb: index !== selectedTitles.length - 1 ? 2 : 0 }}
                    cover={thumbnailService.GetObjectPosterUrl(title.id, ThumbnailSize.small)}
                    title={title.name}
                    alreadyAddedToRequest
                    contents={[]}
                    onRemoveRequestClick={() => dispatch(RemoveTitleByName(title.name))}
                  />
                ))}
              </Box>
            )}
          </>
        )}
        {activeStep === 1 && (
          <>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
              {t("panel:newRequestPanel.body.selectConfigurationPack")}
            </Typography>
            <OutlinedInput
              fullWidth
              placeholder={t("field:placeholder.searchPacks")}
              endAdornment={<SearchIcon />}
              value={searchPackValue}
              onChange={(event) => setSearchPackValue(event.target.value)}
            />
            <Typography variant="h6" color="text.tier" mb={1.5} mt={3}>
              {t("component:body.existingsPacks", { count: fetchedPacks.length })}
            </Typography>
            <Stack spacing={1}>
              {fetchedPacks
                .filter((p) => p.name?.toLocaleLowerCase().includes(searchPackValue.toLocaleLowerCase()))
                .map((pack) => (
                  <PackCard variant="outlined" key={pack.id} aria-selected={selectedPackId === pack.id}>
                    <CardActionArea onClick={() => handlePackOnClick(pack)}>
                      <Stack>
                        <Typography fontWeight={500} mb={0.5}>
                          {pack.name}
                        </Typography>

                        <Typography variant="body2" color="text.tier">
                          {pack.description}
                        </Typography>
                      </Stack>
                      <CheckIcon color="primary" />
                    </CardActionArea>
                  </PackCard>
                ))}
            </Stack>
          </>
        )}
      </Box>
    </DrawerWrapper>
  );
};

export default NewRequestPanel;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  ".MuiIconButton-root": {
    transform: "none !important",
    svg: {
      color: `${theme.palette.primary.main} !important`,
    },
  },
})) as typeof Autocomplete;

const PackCard = styled(Card)(({ theme }) => ({
  ".MuiCardActionArea-root": {
    display: "flex",
    justifyContent: "space-between",
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  },
  ".MuiSvgIcon-root": {
    opacity: 0,
  },
  "&[aria-selected=true]": {
    ".MuiSvgIcon-root": {
      opacity: 1,
    },
  },
}));
